import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { OreNavBarHeaderDivider } from '@edenredespana/oreneta';
import { MultiWebClientMerchantModel, MultiWebClientSubItemModel } from '@/User/domain';
import { useMasterSelectedMerchant } from './hooks/useMasterSelectedMerchant';
import { useMasterDropdownPosition } from './hooks/useMasterDropdownPosition';
import { useMasterDropdownOpen } from './hooks/useMasterDropdownOpen';
import { useMasterMerchantFilter } from './hooks/useMasterMerchantFilter';
import { MasterSearchBar } from './molecules/MasterSearchBar';
import { MasterEmptyState } from './atoms/MasterEmptyState';
import { MasterDropdownTrigger } from './atoms/MasterDropdownTrigger';
import { MasterMerchantList } from './molecules/MasterMerchantList';
import { DROPDOWN_STYLES } from './constants/masterStyles';
import { useTranslation } from 'react-i18next';
import { headerNavbarTranslation } from '../navigation/navigationTranslation';

export const MasterDropdownSearch = (props:{
  userMultiWeb: MultiWebClientMerchantModel[], 
  isNursery: boolean,
  setActiveMultiWebMerchant: (activeMerchantId: number) => Promise<boolean>
}) => {
  const { t } = useTranslation();
  const { userMultiWeb, setActiveMultiWebMerchant } = props;
  const [search, setSearch] = useState('');
  const [hoveredMainKey, setHoveredMainKey] = useState<string | null>(null);
  const [hoveredSubKey, setHoveredSubKey] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set());

  // Refs para el trigger y dropdown
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Hooks personalizados
  const { selected, handleSetSelected: baseHandleSetSelected } = useMasterSelectedMerchant(userMultiWeb, setActiveMultiWebMerchant);
  const { open, setOpen } = useMasterDropdownOpen(triggerRef, dropdownRef);
  const dropdownPos = useMasterDropdownPosition(open, triggerRef);
  const { filteredMerchants, expandedParents } = useMasterMerchantFilter(userMultiWeb, search);
  const masterMerchant = filteredMerchants.filter(master => master.isMasterGroup);
  const nonMasterMerchant = filteredMerchants.filter(merchantParent => !merchantParent.isMasterGroup);

  // Sincronizar expandedKeys con los padres que tienen hijos coincidentes
  useEffect(() => {
    if (search.trim().length >= 3) {
      setExpandedKeys(expandedParents);
    } else {
      setExpandedKeys(new Set());
    }
  }, [search, expandedParents]);

  // Wrapper para handleSetSelected que también cierra el dropdown
  const handleSetSelected = async (item: MultiWebClientMerchantModel | MultiWebClientSubItemModel) => {
    setOpen(false);
    await baseHandleSetSelected(item);
  };

  return (
    <OreNavBarHeaderDivider>
      <div ref={triggerRef}>
        <MasterDropdownTrigger
          selected={selected}
          open={open}
          onClick={() => setOpen(o => !o)}
        />
      </div>
      {/* Dropdown usando portal para evitar problemas de stacking/contexto */}
      {open && createPortal(
        <div 
          ref={dropdownRef}
          style={{
            ...DROPDOWN_STYLES.dropdownContainer,
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}>
          <MasterSearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(headerNavbarTranslation.DropdownSearch.placeholder)}
          />
          <div style={DROPDOWN_STYLES.divider} />
          <div style={DROPDOWN_STYLES.contentSection}>
            {masterMerchant.length > 0 ? (
              <MasterMerchantList
                merchants={masterMerchant}
                hoveredMainKey={hoveredMainKey}
                hoveredSubKey={hoveredSubKey}
                expandedKeys={expandedKeys}
                onMerchantClick={handleSetSelected}
                onSubItemClick={handleSetSelected}
                onMerchantMouseEnter={setHoveredMainKey}
                onMerchantMouseLeave={() => setHoveredMainKey(null)}
                onSubItemMouseEnter={setHoveredSubKey}
                onSubItemMouseLeave={() => setHoveredSubKey(null)}
                onToggleExpand={(code) => {
                  setExpandedKeys(prev => {
                    const next = new Set(prev);
                    if (next.has(code)) {
                      next.delete(code);
                    } else {
                      next.add(code);
                    }
                    return next;
                  });
                }}
              />
            ) : null}
            <div style={DROPDOWN_STYLES.scrolleableContentSection}>
            {nonMasterMerchant.length > 0 ? (
              <MasterMerchantList
                merchants={nonMasterMerchant}
                hoveredMainKey={hoveredMainKey}
                hoveredSubKey={hoveredSubKey}
                expandedKeys={expandedKeys}
                onMerchantClick={handleSetSelected}
                onSubItemClick={handleSetSelected}
                onMerchantMouseEnter={setHoveredMainKey}
                onMerchantMouseLeave={() => setHoveredMainKey(null)}
                onSubItemMouseEnter={setHoveredSubKey}
                onSubItemMouseLeave={() => setHoveredSubKey(null)}
                onToggleExpand={(code) => {
                  setExpandedKeys(prev => {
                    const next = new Set(prev);
                    if (next.has(code)) {
                      next.delete(code);
                    } else {
                      next.add(code);
                    }
                    return next;
                  });
                }}
              />
            ) : (
              <MasterEmptyState isNursery={props.isNursery} />
            )}
            </div>
            <div style={DROPDOWN_STYLES.spacer} />
          </div>
        </div>,
        document.body
      )}
    </OreNavBarHeaderDivider>
  );
}
